import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import supabase from "./supabaseClient";
import { Button } from "./shadcn/ui/button";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "./shadcn/ui/card";
import { motion } from "framer-motion";
import { MailIcon, ArrowLeftIcon } from "lucide-react";

export default function ConfirmEmail() { 
  const navigate = useNavigate();

  useEffect(() => {
    const checkEmailConfirmation = async () => {
      const { data } = await supabase.auth.getSession();

      if (data?.session?.user?.email_confirmed_at) {
        // If the user's email is confirmed, redirect to CreateProfile
        navigate('/create-profile');
      }
    };

    checkEmailConfirmation();

    // Check every 5 seconds
    const interval = setInterval(checkEmailConfirmation, 5000);

    return () => clearInterval(interval);
  }, [navigate]);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2,
      },
    },
  };

  const handleResendEmail = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: (await supabase.auth.getUser()).data.user.email
      });
      
      if (error) {
        throw error;
      }
      
      // Optionally add a success message here
      alert('Verification email has been resent!');
    } catch (error) {
      console.error("Error resending verification email:", error);
      alert('Failed to resend verification email. Please try again.');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Card className="backdrop-blur-lg bg-background/30 dark:bg-background/20 border-background/50 dark:border-foreground/10">
            <CardHeader className="text-center pb-8">
              <motion.div
                className="flex justify-center mb-6"
                variants={iconVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-deep-royal-blue via-tropical-teal to-crystal-sky rounded-full blur-md opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
                  <div className="relative p-6 bg-background/50 dark:bg-background/30 rounded-full">
                    <MailIcon className="w-12 h-12 text-tropical-teal" />
                  </div>
                </div>
              </motion.div>
              <CardTitle className="text-2xl font-bold mb-2">Check your email</CardTitle>
              <CardDescription className="text-base text-muted-foreground">
                We've sent you a verification link to your email address.
                Please click the link to verify your account.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center text-sm text-muted-foreground">
                <p>Check your spam folder if you don't see the email</p>
              </div>
              
              <div className="pt-4">
                <Link to="/login">
                  <Button
                    variant="outline"
                    className="w-full h-12 bg-background/50 dark:bg-background/30 border-background/50 dark:border-foreground/10 backdrop-blur-sm hover:bg-background/70 hover:text-tropical-teal dark:hover:bg-background/50 hover:border-tropical-teal group"
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
